// "Comments & feedback" panel — anyone viewing a building's asbestos register
// (internal staff or a client contact) can leave feedback; internal staff can
// remove a comment. Posting only needs view access to the register.
import { useEffect, useState } from "react";
import { X, MessageSquare, Send, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const ACCENT = "#C8632A";

export function AsbestosFeedbackDialog({ buildingId, canModerate, onClose }) {
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");
    const [posting, setPosting] = useState(false);

    function load() {
        api.get(`/asbestos/building/${buildingId}/comments`)
            .then((r) => setComments(r.data || []))
            .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not load feedback"))
            .finally(() => setLoading(false));
    }
    useEffect(load, [buildingId]);

    async function submit() {
        const text = message.trim();
        if (!text) return;
        setPosting(true);
        try {
            const { data } = await api.post(`/asbestos/building/${buildingId}/comments`, { message: text });
            setComments((c) => [data, ...c]);
            setMessage("");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not submit feedback");
        } finally {
            setPosting(false);
        }
    }

    async function remove(id) {
        try {
            await api.delete(`/asbestos/comments/${id}`);
            setComments((c) => c.filter((x) => x.id !== id));
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not remove comment");
        }
    }

    function fmt(d) {
        try { return new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
        catch { return d; }
    }

    return (
        <div className="fixed inset-0 z-[85] grid place-items-center p-4" style={{ background: "rgba(20,18,16,0.45)" }} onClick={onClose}>
            <div data-testid="asbestos-feedback-dialog" className="bg-white rounded-[14px] w-full max-w-md shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: "80vh" }} onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3.5 flex items-center justify-between border-b shrink-0" style={{ borderColor: "#F0EDE8" }}>
                    <h3 className="text-[14px] font-semibold text-[#1F1F1F] flex items-center gap-2"><MessageSquare className="w-4 h-4" style={{ color: ACCENT }} /> Comments &amp; feedback</h3>
                    <button data-testid="asbestos-feedback-close" onClick={onClose}><X className="w-4 h-4" style={{ color: "#9A968F" }} /></button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
                    {loading ? (
                        <div className="text-[12px] text-center py-6" style={{ color: "#9A968F" }}>Loading…</div>
                    ) : comments.length === 0 ? (
                        <div data-testid="asbestos-feedback-empty" className="text-[12px] text-center py-6" style={{ color: "#9A968F" }}>
                            No comments yet — leave the first bit of feedback below.
                        </div>
                    ) : comments.map((c) => (
                        <div key={c.id} data-testid={`asbestos-feedback-${c.id}`} className="rounded-[10px] p-3" style={{ background: "#F6F4F0" }}>
                            <div className="flex items-center justify-between">
                                <div className="text-[12px] font-semibold text-[#1F1F1F]">{c.author_name} <span className="font-normal text-[10.5px]" style={{ color: "#9A968F" }}>· {c.author_role.replace("_", " ")}</span></div>
                                {canModerate && (
                                    <button data-testid={`asbestos-feedback-delete-${c.id}`} onClick={() => remove(c.id)} title="Remove comment">
                                        <Trash2 className="w-3.5 h-3.5" style={{ color: "#B33A2E" }} />
                                    </button>
                                )}
                            </div>
                            <p className="text-[12.5px] mt-1 whitespace-pre-wrap" style={{ color: "#3A3530" }}>{c.message}</p>
                            <div className="text-[10.5px] mt-1" style={{ color: "#9A968F" }}>{fmt(c.created_at)}</div>
                        </div>
                    ))}
                </div>

                <div className="p-3.5 border-t shrink-0 flex items-end gap-2" style={{ borderColor: "#F0EDE8" }}>
                    <textarea data-testid="asbestos-feedback-input" rows={2} value={message} onChange={(e) => setMessage(e.target.value)}
                        placeholder="Add a comment or feedback on this register…"
                        className="flex-1 text-[12.5px] rounded-[8px] border px-2.5 py-2 resize-none" style={{ borderColor: "#E2DDD5" }} />
                    <button data-testid="asbestos-feedback-submit" disabled={posting || !message.trim()} onClick={submit}
                        className="h-9 w-9 rounded-[8px] grid place-items-center text-white disabled:opacity-50 shrink-0" style={{ background: ACCENT }}>
                        {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                </div>
            </div>
        </div>
    );
}
